import { useContext, useEffect, useRef } from 'react'
import type { ReactElement, ReactNode } from 'react'

import type { NextPage } from 'next'
import type { AppProps } from 'next/app'
import { useRouter } from 'next/router'

import CssBaseline from '@mui/material/CssBaseline'
import { ThemeProvider } from '@mui/material/styles'
import { Auth0Provider, useAuth0 } from 'shim/auth0-react'

import ErrorBoundary from 'common/components/errors/ErrorBoundary'
import UnleashProvider from 'common/components/featuretoggle/UnleashProvider'
import { getLayout } from 'common/components/layouts/AppLayout'
import AppMeta from 'common/components/layouts/metas/AppMeta'
import LoadingOverlay from 'common/components/util/LoadingOverlay'
import { SectionUserContext } from 'common/contexts/SectionUserContext'
import SectionUserProvider from 'common/providers/SectionUserProvider'
import SelectedProjectEnvironmentProvider from 'common/providers/SelectedProjectEnvironmentProvider'
import akamaiTheme from 'common/theme/akamaiTheme'
import lumenTheme from 'common/theme/lumenTheme'
import sectionTheme from 'common/theme/sectionTheme'
import { BannerProvider } from 'modules/notifications/banners/BannerProvider'
import VerificationErrorOverlay from 'modules/user/VerificationErrorOverlay'
import AuthErrorOverlay from 'modules/user/components/AuthErrorOverlay'
import {
  CONSOLE_AUTH0_AUTHENTICATION_ERROR_MESSAGE,
  CONSOLE_GRAPHQL_CREATE_USER_ACCOUNT_DISABLED_ERROR,
  CONSOLE_GRAPHQL_USER_QUEUE_MESSAGE,
} from 'modules/user/constants'
import useSectionUser from 'modules/user/hooks/useSectionUser'

export type GetAppLayout = (page: ReactElement) => ReactNode

export type AppPage<P = {}, IP = P> = NextPage<P, IP> & {
  getLayout?: GetAppLayout
  isPublic?: boolean
}

type Props<P = {}> = AppProps<P> & {
  Component: AppPage<P>
}

const defaultGetLayout: GetAppLayout = getLayout

function App({ Component, pageProps }: Props) {
  const { isLoading: authLoading, error: authError, isAuthenticated, loginWithRedirect, user } = useAuth0()
  const { loading: sectionUserLoading, user: sectionUser, error: sectionUserError } = useSectionUser()
  const authRedirectCalled = useRef(false)
  const router = useRouter()
  const { accountId, refererPage } = router.query
  const { selectedAccount, setSelectedAccount, setSectionUser, selectedTheme, setSelectedTheme } =
    useContext(SectionUserContext)

  useEffect(() => {
    if (refererPage) {
      typeof localStorage !== 'undefined' && localStorage.setItem('refererPage', refererPage as string)
    }
  }, [refererPage])

  useEffect(() => {
    if (sectionUser?.accounts) {
      setSectionUser(sectionUser)
    }
  }, [sectionUser, setSectionUser])

  useEffect(() => {
    if (
      (sectionUser?.accounts && !selectedAccount) ||
      (sectionUser?.accounts && selectedAccount && accountId && selectedAccount !== accountId)
    ) {
      // Set contexts
      const defaultAccount = sectionUser?.accounts?.find(
        (account: { name: string; id: string; type: string; tenantID: string }) => account.id === accountId
      )

      setSelectedTheme(defaultAccount ? defaultAccount.tenantID : sectionUser?.accounts[0]?.tenantID)
      setSelectedAccount(defaultAccount ? defaultAccount.id : sectionUser?.accounts[0]?.id)
    }
  }, [sectionUser, accountId, setSelectedAccount, selectedAccount, setSelectedTheme])

  // Get page layout defined at the page level, if available;
  // otherwise use default layout
  const getLayout = Component.getLayout ?? defaultGetLayout

  // Get the page (or overlay if loading or errored)
  let pageComponent = null
  // page is not public but authentication state is still loading
  // the `loading` condition is a carry-over from legacy
  // data-fetching strategies. Will show the loading overlay if a legacy
  // component makes a data request
  if (
    (!Component.isPublic && authLoading) ||
    ((isAuthenticated || process.env.NEXT_PUBLIC_BYPASS_AUTH_STATE) &&
      (sectionUserLoading || (!sectionUserError && !sectionUser)))
  )
    pageComponent = <LoadingOverlay />
  else if (authError) {
    pageComponent = <AuthErrorOverlay messageTitle={CONSOLE_AUTH0_AUTHENTICATION_ERROR_MESSAGE} />
  } else if (sectionUserError && sectionUserError[0]?.message === CONSOLE_GRAPHQL_CREATE_USER_ACCOUNT_DISABLED_ERROR) {
    pageComponent = <AuthErrorOverlay messageTitle={CONSOLE_GRAPHQL_USER_QUEUE_MESSAGE} />
  } else if (
    isAuthenticated &&
    !user?.email_verified &&
    router.pathname !== '/console-auth/verify' &&
    router.pathname !== '/console-auth/verify/'
  ) {
    pageComponent = <VerificationErrorOverlay />
  }

  // page is either public or the user is confirmed to be authenticated
  else if (Component.isPublic === true || isAuthenticated || process.env.NEXT_PUBLIC_BYPASS_AUTH_STATE) {
    pageComponent = getLayout(<Component {...pageProps} />)
  } else {
    // page is not public and the user is not authenticated
    // Check if this is already called so that we don't fire multiple redirect events
    if (!authRedirectCalled.current) {
      if (router.pathname === '/signup' || router.pathname === '/signup/') {
        // check for signup route before redirect to login page
        loginWithRedirect({
          appState: {
            returnTo: typeof window !== 'undefined' && window.location.href,
            target: typeof window !== 'undefined' && window.location.search,
          },
          screen_hint: 'signup',
        })
      } else {
        loginWithRedirect({
          appState: {
            returnTo: typeof window !== 'undefined' && window.location.href,
            target: typeof window !== 'undefined' && window.location.search,
          },
        })
      }
      authRedirectCalled.current = true
    }
    pageComponent = <LoadingOverlay />
  }

  let theme = sectionTheme
  if (selectedTheme === '6') {
    theme = lumenTheme
  } else if (selectedTheme === '19') {
    theme = akamaiTheme
  }

  return (
    <>
      <AppMeta />
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <ErrorBoundary>
          <BannerProvider autoHideDuration={8500}>{pageComponent}</BannerProvider>
        </ErrorBoundary>
      </ThemeProvider>
    </>
  )
}

export default function AppProvider(props: Props) {
  const router = useRouter()
  const { push } = router

  return (
    <Auth0Provider
      audience={process.env.NEXT_PUBLIC_AUTH_AUDIENCE || ''}
      clientId={process.env.NEXT_PUBLIC_AUTH_CLIENT_ID || ''}
      domain={process.env.NEXT_PUBLIC_AUTH_DOMAIN || ''}
      onRedirectCallback={(appState) => {
        push(appState?.returnTo || window.location.origin)
      }}
      redirectUri={typeof window !== 'undefined' ? window.location.origin : ''}
    >
      <SectionUserProvider>
        <SelectedProjectEnvironmentProvider>
          <UnleashProvider>
            <App {...props} />
          </UnleashProvider>
        </SelectedProjectEnvironmentProvider>
      </SectionUserProvider>
    </Auth0Provider>
  )
}
